import type { GetStaticPropsContext } from 'next';

import { Box, Heading } from '@chakra-ui/react';

import { BlueWaveHero } from '~/components/blue-wave-hero';
import { ContentContainer } from '~/components/container';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { BottomSection } from '~/features/news/components/bottom-section';
import { NewsArticlesSection } from '~/features/news/components/news-articles-section';
import { NewsPressSection } from '~/features/news/components/news-press-section';
import { getNews } from '~/features/news/controllers/get-news';
import { NewsList } from '~/features/news/models/news';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

export default function News({ page, news }: { page: Page; news: NewsList }) {
  const articles = news.filter((newsItem) => newsItem.newsType === 'article');
  const press = news.filter((newsItem) => newsItem.newsType === 'press');

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <BlueWaveHero>
          <Heading as='h1' size='marketing-4xl' textAlign='center'>
            Recent News
          </Heading>
        </BlueWaveHero>
        <ContentContainer
          padding={{
            base: '40px 16px',
            md: '80px 16px',
            xl: '80px 0px',
          }}
        >
          <NewsArticlesSection articles={articles} />
        </ContentContainer>
        <Box bg='accent.sky.100'>
          <ContentContainer
            padding={{
              base: '40px 16px',
              md: '80px 16px',
              xl: '80px 0px',
            }}
          >
            <NewsPressSection press={press} />
          </ContentContainer>
        </Box>
        <ContentContainer
          padding={{
            base: '40px 16px 80px 16px',
            md: '80px 16px 120px 16px',
            xl: '80px 0px 120px 0px',
          }}
        >
          <BottomSection />
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, news] = await Promise.all([getPage('/news'), getNews()]);

  return {
    props: {
      page,
      news,
    },
  };
}
